import {
  AvailableMcpServer,
  CatalogExternalMcpServer,
  CreateExternalMcpServerRequest,
  DiscoverExternalMcpOAuthResponse,
  InstallExternalMcpServerRequest,
  MCPServer,
  PackageInfo,
  SaveExternalMcpServerSecretsRequest,
  StartExternalMcpOAuthRequest,
  StartExternalMcpOAuthResponse,
  Tool,
  ToolMethod,
  UpdateExternalMcpServerInstallRequest,
  UserVisibleMcpServer,
  McpToolCallRequest,
  McpToolCallFailureResponse,
  RequestMcpServerInstallPayload
} from '@/types/mcp'
import {
  getServers,
  getUserMcpServers,
  getAdminMcpServers,
  adminGetSharedServerDeleteImpact,
  getUserMcpServer,
  getUserMcpServerTools,
  createExternalMcpServer as apiCreateExternalMcpServer,
  discoverExternalMcpOAuth as apiDiscoverExternalMcpOAuth,
  installExternalMcpServer as apiInstallExternalMcpServer,
  updateExternalMcpServerInstall as apiUpdateExternalMcpServerInstall,
  uninstallExternalMcpServer as apiUninstallExternalMcpServer,
  saveExternalMcpServerSecrets as apiSaveExternalMcpServerSecrets,
  startExternalMcpOAuth as apiStartExternalMcpOAuth,
  refreshExternalMcpServer as apiRefreshExternalMcpServer,
  getAvailableMcpServers,
  getPackages,
  installPackage,
  uninstallPackage,
  checkForUpdates,
  upgradePackage,
  upgradeAllPackages,
  adminDeleteServer,
  adminEnableServer,
  adminDisableServer,
  getTools,
  callMcpTool as apiCallMcpTool,
  requestMcpServerInstall as apiRequestMcpServerInstall
} from '@/utils/api'
import { isApiError } from '@/types/api'
import { pruneUnavailableServerTools } from '@/utils/mcpState'
import { Commit, Dispatch } from 'vuex'

export interface McpToolParameter {
  name: string
  type: string
  description: string
  required: boolean
  defaultValue?: unknown
  exampleValue?: unknown
  enumValues?: unknown[]
}

export interface McpTool {
  id: string
  name: string
  description: string
  parameters: McpToolParameter[]
  example?: string
}

export interface McpServer {
  id: string
  name: string
  displayName?: string
  commandString: string
  description: string
  authToken?: string        // Deprecated, kept for backward compatibility
  status?: string
  timestamp: number
  enabled?: boolean
  logs?: string[]
  command?: string
  args?: string[]
  secretName?: string       // ← KEEP for backward compatibility
  secretNames?: string[]    // ← ADD new property
  configuredSecretNames?: string[]
  source?: 'platform' | 'external'
  transportType?: 'stdio' | 'streamable_http'
  installed?: boolean
  authMode?: 'none' | 'oauth2'
  authRequired?: boolean
  authState?: 'not_required' | 'not_connected' | 'connected' | 'reauth_required' | 'error'
  secretFields?: Array<{ name: string; label: string; description: string; required: boolean; inputType: 'password' }>
  homepageUrl?: string
  repositoryUrl?: string
  licenseName?: string
  canInstall?: boolean
  canUninstall?: boolean
  canConfigure?: boolean
  canManagePlatformServer?: boolean
  url?: string
  statusMessage?: string
  oauthTemplate?: CreateExternalMcpServerRequest['oauthTemplate']
  oauthClientConfig?: {
    clientId?: string
    scopes?: string[]
  }
  toolsList?: ToolMethod[]
  npmPackage?: string
  npmPackageUrl?: string
}

export interface McpState {
  servers: Record<string, McpServer>
  tools: Record<string, Record<string, McpTool>> // { serverId: { toolId: toolData } }
  error: string | null
  loading: boolean
  availableServers: AvailableMcpServer[]
  availableServersError: string | null
  availableServersLoading: boolean
  availableServersSearchQuery: string
  packages: PackageInfo[]
  packagesError: string | null
  packagesLoading: boolean
  allTools: Tool[]
  toolCallLoadingByKey: Record<string, boolean>
  toolCallErrorByKey: Record<string, McpToolCallFailureResponse | null>
  toolCallResultByKey: Record<string, unknown>
  toolCallArgsByKey: Record<string, Record<string, unknown>>
}

function buildToolCallKey(serverName: string, methodName: string): string {
  return `${serverName}::${methodName}`
}

function reconcileMcpServerTools(state: McpState): void {
  state.tools = pruneUnavailableServerTools(state.servers, state.tools)
}

export const mcp = () => ({
  state: <McpState>{
    servers: {},
    tools: {},
    error: null,
    loading: false,
    availableServers: [],
    availableServersError: null,
    availableServersLoading: false,
    availableServersSearchQuery: '',
    packages: [],
    packagesError: null,
    packagesLoading: false,
    allTools: [],
    toolCallLoadingByKey: {},
    toolCallErrorByKey: {},
    toolCallResultByKey: {},
    toolCallArgsByKey: {}
  },
  mutations: {
    setMcpError(state: McpState, error: string | null) {
      state.error = error
    },
    setMcpLoading(state: McpState, loading: boolean) {
      state.loading = loading
    },
    setMcpServers(state: McpState, servers: Record<string, McpServer>) {
      state.servers = servers
      reconcileMcpServerTools(state)
    },
    addMcpServer(state: McpState, server: McpServer) {
      state.servers = { ...state.servers, [server.id]: server }
      reconcileMcpServerTools(state)
    },
    updateMcpServer(state: McpState, server: McpServer) {
      state.servers = { ...state.servers, [server.id]: server }
      reconcileMcpServerTools(state)
    },
    mergeMcpServers(state: McpState, servers: Record<string, McpServer>) {
      state.servers = { ...state.servers, ...servers }
      reconcileMcpServerTools(state)
    },
    deleteMcpServer(state: McpState, serverId: string) {
      const { [serverId]: _, ...remainingServers } = state.servers
      state.servers = remainingServers
      reconcileMcpServerTools(state)
    },
    setMcpTools(state: McpState, { serverId, tools }: { serverId: string; tools: Record<string, McpTool> }) {
      state.tools = { ...state.tools, [serverId]: tools }
      reconcileMcpServerTools(state)
    },
    // Available MCP servers mutations
    setAvailableServers(state: McpState, servers: AvailableMcpServer[]) {
      state.availableServers = servers
    },
    setAvailableServersError(state: McpState, error: string | null) {
      state.availableServersError = error
    },
    setAvailableServersLoading(state: McpState, loading: boolean) {
      state.availableServersLoading = loading
    },
    setAvailableServersSearchQuery(state: McpState, query: string) {
      state.availableServersSearchQuery = query
    },

    // MCP Packages mutations
    setPackages(state: McpState, packages: PackageInfo[]) {
      state.packages = packages
    },
    setPackagesError(state: McpState, error: string | null) {
      state.packagesError = error
    },
    setPackagesLoading(state: McpState, loading: boolean) {
      state.packagesLoading = loading
    },
    setTools(state: McpState, tools: Tool[]) {
      // this is a separate property for now, but to be efficient we should create this from the tools property
      state.allTools = tools
    },
    setToolCallLoading(state: McpState, { callKey, loading }: { callKey: string; loading: boolean }) {
      state.toolCallLoadingByKey = { ...state.toolCallLoadingByKey, [callKey]: loading }
    },
    setToolCallError(state: McpState, { callKey, error }: { callKey: string; error: McpToolCallFailureResponse | null }) {
      state.toolCallErrorByKey = { ...state.toolCallErrorByKey, [callKey]: error }
    },
    setToolCallResult(state: McpState, { callKey, result }: { callKey: string; result: unknown }) {
      state.toolCallResultByKey = { ...state.toolCallResultByKey, [callKey]: result }
    },
    setToolCallArgs(state: McpState, { callKey, args }: { callKey: string; args: Record<string, unknown> }) {
      state.toolCallArgsByKey = { ...state.toolCallArgsByKey, [callKey]: args }
    },
    clearToolCallState(state: McpState, callKey: string) {
      const { [callKey]: _l, ...loadingRest } = state.toolCallLoadingByKey
      const { [callKey]: _e, ...errorRest } = state.toolCallErrorByKey
      const { [callKey]: _r, ...resultRest } = state.toolCallResultByKey
      const { [callKey]: _a, ...argsRest } = state.toolCallArgsByKey
      state.toolCallLoadingByKey = loadingRest
      state.toolCallErrorByKey = errorRest
      state.toolCallResultByKey = resultRest
      state.toolCallArgsByKey = argsRest
    },
    clearAllToolCallState(state: McpState) {
      state.toolCallLoadingByKey = {}
      state.toolCallErrorByKey = {}
      state.toolCallResultByKey = {}
      state.toolCallArgsByKey = {}
    }
  },
  actions: {
    async listTools({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const response = await getTools()
      if (isApiError(response)) {
        console.error('Error fetching tools:', response.message)
        dispatch('updateSnackbar', { text: `MCP Servers Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Servers Error', content: response }, { root: true })
        return []
      }
      if (response.success) {
        commit('setTools', response.tools)
        return response.tools
      }
      return []
    },
    async fetchUserMcpServers({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setMcpLoading', true)
      commit('setMcpError', null)

      const response = await getUserMcpServers()

      if (isApiError(response)) {
        console.error('Error fetching MCP servers:', response.message)
        commit('setMcpError', response.message)
        dispatch('updateSnackbar', { text: `MCP Servers Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Servers Error', content: response }, { root: true })
        commit('setMcpLoading', false)
        return
      }

      if (response.success) {
        const servers: Record<string, McpServer> = {}

        response.servers.forEach((server: UserVisibleMcpServer) => {
          const serverId = server.name
          const npmPackageInfo = server.args ? detectNpmPackage(server.args) : null
          const commandString =
            server.command && server.args && server.args.length > 0 ? `${server.command} ${server.args.join(' ')}` : server.command || ''

          servers[serverId] = {
            id: serverId,
            name: server.name,
            displayName: server.displayName,
            commandString,
            command: server.command,
            args: server.args,
            description: server.description,
            status: server.status,
            enabled: server.enabled,
            logs: server.logs,
            source: server.source,
            transportType: server.transportType,
            installed: server.installed,
            authMode: server.authMode,
            authRequired: server.authRequired,
            authState: server.authState,
            secretFields: server.secretFields,
            secretNames: server.secretNames,
            configuredSecretNames: server.configuredSecretNames,
            homepageUrl: server.homepageUrl,
            repositoryUrl: server.repositoryUrl,
            licenseName: server.licenseName,
            canInstall: server.canInstall,
            canUninstall: server.canUninstall,
            canConfigure: server.canConfigure,
            canManagePlatformServer: server.canManagePlatformServer,
            url: server.url,
            statusMessage: server.statusMessage,
            oauthTemplate: server.oauthTemplate,
            oauthClientConfig: server.oauthClientConfig,
            toolsList: server.toolsList,
            npmPackage: npmPackageInfo?.name,
            npmPackageUrl: npmPackageInfo?.url,
            timestamp: Date.now()
          }
        })

        commit('setMcpServers', servers)
        dispatch(
          'addLogEvent',
          {
            title: 'MCP Servers Fetched',
            content: { serverCount: Object.keys(servers).length }
          },
          { root: true }
        )
      } else {
        commit('setMcpError', 'Failed to fetch MCP servers')
        dispatch('updateSnackbar', { text: 'Failed to fetch MCP servers' }, { root: true })
      }

      commit('setMcpLoading', false)
    },
    async fetchMcpServers({ dispatch }: { dispatch: Dispatch }) {
      return dispatch('fetchUserMcpServers')
    },
    async fetchAdminPlatformServers(
      { commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch }
    ) {
      const response = await getAdminMcpServers()

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Admin MCP Servers Error: ${response.message}` }, { root: true })
        return
      }

      if (!response.success) {
        return
      }

      const mergedServers: Record<string, McpServer> = {}
      response.servers.forEach((server: any) => {
        const source = server.source
        if (source !== 'platform') {
          return
        }

        const serverId = server.name
        const existing = state.servers[serverId]
        const npmPackageInfo = server.args ? detectNpmPackage(server.args) : null
        const commandString =
          server.command && server.args && server.args.length > 0 ? `${server.command} ${server.args.join(' ')}` : server.command || ''

        mergedServers[serverId] = {
          ...(existing ?? { id: serverId, timestamp: Date.now() }),
          id: serverId,
          name: server.name,
          displayName: server.displayName ?? server.name,
          commandString,
          command: server.command,
          args: server.args,
          description: server.description || '',
          status: server.status,
          enabled: server.enabled,
          logs: server.logs,
          source,
          secretName: server.secretName,
          secretNames: server.secretNames,
          transportType: server.transportType,
          installed: server.installed,
          authMode: server.authMode,
          authRequired: server.authRequired,
          authState: server.authState,
          secretFields: server.secretFields,
          configuredSecretNames: server.configuredSecretNames,
          homepageUrl: server.homepageUrl,
          repositoryUrl: server.repositoryUrl,
          licenseName: server.licenseName,
          canInstall: server.canInstall,
          canUninstall: server.canUninstall,
          canConfigure: server.canConfigure,
          canManagePlatformServer: server.canManagePlatformServer,
          url: server.url,
          statusMessage: server.statusMessage,
          toolsList: server.toolsList,
          npmPackage: npmPackageInfo?.name,
          npmPackageUrl: npmPackageInfo?.url,
          timestamp: Date.now()
        }
      })

      commit('mergeMcpServers', mergedServers)
    },
    async fetchUserServer(
      { commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch },
      serverName: string
    ) {
      const response = await getUserMcpServer(serverName)

      if (isApiError(response)) {
        commit('setMcpError', response.message)
        dispatch('updateSnackbar', { text: `MCP Server Error: ${response.message}` }, { root: true })
        return null
      }

      if (!response.success) {
        return null
      }

      const existing = state.servers[serverName]
      const server: McpServer = {
        ...(existing ?? { id: response.server.name, timestamp: Date.now(), commandString: '' }),
        ...response.server,
        id: response.server.name,
        displayName: response.server.displayName,
        secretNames: response.server.secretNames,
        configuredSecretNames: response.server.configuredSecretNames,
        toolsList: response.server.toolsList,
        timestamp: Date.now(),
        commandString: existing?.commandString || ''
      }
      commit('updateMcpServer', server)
      return server
    },
    async fetchMcpTools(
      { commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch },
      serverId: string
    ) {
      commit('setMcpLoading', true)
      commit('setMcpError', null)

      const server = state.servers[serverId]
      if (!server) {
        commit('setMcpLoading', false)
        return {}
      }

      const response = await getUserMcpServerTools(server.name)

      if (isApiError(response)) {
        console.error('Error fetching MCP tools:', response.message)
        commit('setMcpError', response.message)
        dispatch('updateSnackbar', { text: `MCP Tools Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Tools Error', content: response }, { root: true })
        commit('setMcpTools', { serverId, tools: {} })
        commit('setMcpLoading', false)
        return {}
      }

      if (response.success) {
        if (response.tools && response.tools.length > 0) {
          const tools = transformToolsToMcpTools(response.tools)
          commit('setMcpTools', { serverId, tools })
          commit('setMcpLoading', false)
          return tools
        }
      }

      // If we couldn't find tools for this server, return empty object
      commit('setMcpTools', { serverId, tools: {} })
      commit('setMcpLoading', false)
      return {}
    },
    async fetchUserServerTools({ dispatch }: { dispatch: Dispatch }, serverName: string) {
      return dispatch('fetchMcpTools', serverName)
    },
    async addMcpServer({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, { id }: { id: string }) {
      const newServer: McpServer = {
        id,
        name: '',
        commandString: '',
        description: '',
        timestamp: Date.now(),
        enabled: true,
        logs: [],
        command: '',
        args: []
      }
      commit('addMcpServer', newServer)
      dispatch('addLogEvent', { title: 'MCP Server Added', content: { id: id } }, { root: true })
      return newServer
    },
    async updateMcpServer(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      server: Partial<McpServer> & { id: string }
    ) {
      try {
        // In a real implementation, this would update the server in an API
        // For now, we'll just update the local state
        const updatedServer: McpServer = {
          ...server,
          timestamp: Date.now()
        } as McpServer

        commit('updateMcpServer', updatedServer)
        dispatch('updateSnackbar', { text: `Saved MCP server: ${server.name}` })
        dispatch('addLogEvent', { title: 'MCP Server Updated', content: updatedServer }, { root: true })
        return true
      } catch (error) {
        console.error('Error updating MCP server:', error)
        dispatch('updateSnackbar', { text: `Error saving MCP server: ${error}` })
        return false
      }
    },
    async deleteMcpServer(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: McpState },
      serverId: string
    ) {
      const server = state.servers[serverId]
      if (!server) {
        // If server not found in state, just remove from local state
        commit('deleteMcpServer', serverId)
        dispatch('updateSnackbar', { text: `Deleted MCP server` }, { root: true })
        return true
      }

      const response = await adminDeleteServer(server.name)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Delete MCP Server Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Delete MCP Server Error', content: response }, { root: true })
        return false
      }

      if (response.success) {
        commit('deleteMcpServer', serverId)
        await dispatch('fetchUserMcpServers')
        await dispatch('fetchAdminPlatformServers')
        dispatch('updateSnackbar', { text: `Deleted MCP server: ${server.name}` }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Server Deleted', content: { name: server.name } }, { root: true })
        return true
      } else {
        dispatch(
          'updateSnackbar',
          { text: `Failed to delete MCP server: ${server.name}`, color: 'error' },
          { root: true }
        )
        return false
      }
    },

    async fetchAdminSharedServerDeleteImpact(
      _context: { dispatch: Dispatch },
      serverName: string
    ) {
      const response = await adminGetSharedServerDeleteImpact(serverName)
      if (isApiError(response)) {
        throw response
      }
      return response.impact
    },

    /**
     * Refreshes both MCP servers and tools for a specific server
     * @param param0
     * @param serverId The ID of the server to refresh tools for, if any
     */
    async refreshMcpServersAndTools({ dispatch }: { dispatch: Dispatch }, serverId?: string) {
      // First refresh the servers list
      await dispatch('fetchMcpServers')

      // Then refresh tools for the specified server if provided
      if (serverId) {
        await dispatch('fetchMcpTools', serverId)
      }
    },

    async enableMcpServer(
      { commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch },
      serverId: string
    ) {
      const server = state.servers[serverId]
      if (!server) {
        dispatch('updateSnackbar', { text: `Server not found`, color: 'error' }, { root: true })
        return false
      }

      const response = await adminEnableServer(server.name)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Enable MCP Server Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Enable MCP Server Error', content: response }, { root: true })
        return false
      }

      if (response.success) {
        // Update server in state
        const updatedServer = {
          ...server,
          enabled: true
        }
        commit('updateMcpServer', updatedServer)
        dispatch('updateSnackbar', { text: `Enabled MCP server: ${server.name}` }, { root: true })

        // Refresh servers and tools to ensure UI is up to date
        await dispatch('refreshMcpServersAndTools', serverId)

        return true
      } else {
        dispatch(
          'updateSnackbar',
          { text: `Failed to enable MCP server: ${server.name}`, color: 'error' },
          { root: true }
        )
        return false
      }
    },

    async disableMcpServer(
      { commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch },
      serverId: string
    ) {
      const server = state.servers[serverId]
      if (!server) {
        dispatch('updateSnackbar', { text: `Server not found`, color: 'error' }, { root: true })
        return false
      }

      const response = await adminDisableServer(server.name)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Disable MCP Server Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Disable MCP Server Error', content: response }, { root: true })
        return false
      }

      if (response.success) {
        // Update server in state
        const updatedServer = {
          ...server,
          enabled: false
        }
        commit('updateMcpServer', updatedServer)
        dispatch('updateSnackbar', { text: `Disabled MCP server: ${server.name}` }, { root: true })

        // Refresh servers and tools to ensure UI is up to date
        await dispatch('refreshMcpServersAndTools', serverId)

        return true
      } else {
        dispatch(
          'updateSnackbar',
          { text: `Failed to disable MCP server: ${server.name}`, color: 'error' },
          { root: true }
        )
        return false
      }
    },
    async testMcpServer(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { id, url, authToken }: { id: string; url: string; authToken?: string }
    ) {
      commit('setMcpLoading', true)
      commit('setMcpError', null)

      const response = await getServers()
      commit('setMcpLoading', false)

      if (isApiError(response)) {
        commit('setMcpError', response.message)
        return { success: false, message: response.message }
      }

      if (response.success) {
        return { success: true, message: 'Connection successful' }
      } else {
        return { success: false, message: 'Failed to connect to MCP server' }
      }
    },

    // Available MCP servers actions
    async fetchAvailableServers({ commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch }) {
      commit('setAvailableServersLoading', true)
      commit('setAvailableServersError', null)

      // Build URL with search query if present
      const searchParam = state.availableServersSearchQuery
        ? `?query=${encodeURIComponent(state.availableServersSearchQuery)}`
        : ''

      const response = await getAvailableMcpServers(searchParam)

      if (isApiError(response)) {
        const errorMsg = 'Failed to fetch available servers: ' + response.message
        console.error(errorMsg)
        commit('setAvailableServersError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
        commit('setAvailableServersLoading', false)
        return
      }

      if (response.success && response.servers && response.servers.length > 0) {
        commit('setAvailableServers', response.servers)
      } else {
        commit('setAvailableServers', [])
        commit('setAvailableServersError', null)
      }

      commit('setAvailableServersLoading', false)
    },

    async searchAvailableServers({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, query: string) {
      commit('setAvailableServersSearchQuery', query)
      return dispatch('fetchAvailableServers')
    },

    async createExternalServer(
      { dispatch }: { dispatch: Dispatch },
      payload: CreateExternalMcpServerRequest
    ): Promise<UserVisibleMcpServer> {
      const response = await apiCreateExternalMcpServer(payload)
      if (isApiError(response)) {
        throw response
      }
      await dispatch('fetchUserMcpServers')
      return response.server
    },

    async discoverExternalServerOAuth(
      _context: { dispatch: Dispatch },
      payload: { url: string; authorizationServerIssuerOverride?: string }
    ): Promise<DiscoverExternalMcpOAuthResponse> {
      const response = await apiDiscoverExternalMcpOAuth({
        url: payload.url,
        ...(payload.authorizationServerIssuerOverride
          ? { authorizationServerIssuerOverride: payload.authorizationServerIssuerOverride }
          : {})
      })
      if (isApiError(response)) {
        throw response
      }
      return response
    },

    async installExternalServer(
      { dispatch }: { dispatch: Dispatch },
      payload: { serverName: string; body: InstallExternalMcpServerRequest }
    ): Promise<UserVisibleMcpServer> {
      const response = await apiInstallExternalMcpServer(payload.serverName, payload.body)
      if (isApiError(response)) {
        throw response
      }
      await dispatch('fetchUserMcpServers')
      return response.server
    },

    async updateExternalServerInstall(
      { dispatch }: { dispatch: Dispatch },
      payload: { serverName: string; body: UpdateExternalMcpServerInstallRequest }
    ): Promise<UserVisibleMcpServer> {
      const response = await apiUpdateExternalMcpServerInstall(payload.serverName, payload.body)
      if (isApiError(response)) {
        throw response
      }
      await dispatch('fetchUserMcpServers')
      return response.server
    },

    async uninstallExternalServer({ dispatch }: { dispatch: Dispatch }, serverName: string): Promise<void> {
      const response = await apiUninstallExternalMcpServer(serverName)
      if (isApiError(response)) {
        throw response
      }
      await dispatch('fetchUserMcpServers')
    },

    async saveExternalServerSecrets(
      { dispatch }: { dispatch: Dispatch },
      payload: { serverName: string; secrets: SaveExternalMcpServerSecretsRequest['secrets'] }
    ): Promise<void> {
      const response = await apiSaveExternalMcpServerSecrets(payload.serverName, { secrets: payload.secrets })
      if (isApiError(response)) {
        throw response
      }
      await dispatch('fetchUserServer', payload.serverName)
    },

    async startExternalServerOAuth(
      _context: { dispatch: Dispatch },
      payload: { serverName: string; body: StartExternalMcpOAuthRequest }
    ): Promise<StartExternalMcpOAuthResponse> {
      const response = await apiStartExternalMcpOAuth(payload.serverName, payload.body)
      if (isApiError(response)) {
        throw response
      }
      return response
    },

    async refreshExternalServer({ dispatch }: { dispatch: Dispatch }, serverName: string): Promise<UserVisibleMcpServer> {
      const response = await apiRefreshExternalMcpServer(serverName)
      if (isApiError(response)) {
        throw response
      }
      await dispatch('fetchUserMcpServers')
      return response.server
    },

    async installServer({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, server: AvailableMcpServer) {
      try {
        // Create a new server with the information from the available server
        const serverId = `server-${Date.now()}`

        // First add the server with basic info
        await dispatch('addMcpServer', { id: serverId })

        // Then update it with the details from the available server
        const newServer: Partial<McpServer> & { id: string } = {
          id: serverId,
          name: server.name,
          description: server.description,
          commandString: '',
          command: '',
          args: [],
          enabled: true
        }

        await dispatch('updateMcpServer', newServer)

        dispatch('updateSnackbar', { text: `Installed MCP server: ${server.name}` })
        return serverId
      } catch (error) {
        console.error('Error installing server:', error)
        dispatch('updateSnackbar', { text: `Error installing server: ${error}`, color: 'error' })
        throw error
      }
    },

    // MCP Package actions
    async fetchPackages({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, checkUpdates: boolean = false) {
      commit('setPackagesLoading', true)
      commit('setPackagesError', null)

      const response = await getPackages(checkUpdates)

      if (isApiError(response)) {
        const errorMsg = 'Failed to fetch packages: ' + response.message
        console.error(errorMsg)
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
        commit('setPackagesLoading', false)
        return
      }

      if (response.success) {
        commit('setPackages', response.packages)
      } else {
        const msg = 'Failed to fetch packages'
        commit('setPackagesError', msg)
        dispatch('updateSnackbar', { text: msg }, { root: true })
      }

      commit('setPackagesLoading', false)
    },

    async installPackage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { name, serverName, version }: { name: string; serverName: string; version?: string }
    ) {
      commit('setPackagesLoading', true)
      commit('setPackagesError', null)

      const response = await installPackage({ name, serverName, version })

      if (isApiError(response)) {
        const errorMsg = `Failed to install package ${name}: ${response.message}`
        console.error(errorMsg)
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return null
      }

      if (response.success) {
        // Refresh packages list
        const packagesResponse = await getPackages()

        if (isApiError(packagesResponse)) {
          console.error('Error refreshing packages list:', packagesResponse.message)
        } else if (packagesResponse.success) {
          commit('setPackages', packagesResponse.packages)
        }

        dispatch('updateSnackbar', { text: `Package ${name} installed successfully` }, { root: true })
        dispatch(
          'addLogEvent',
          {
            title: 'Package Installed',
            content: {
              name: name,
              serverName: serverName,
              version: version
            }
          },
          { root: true }
        )
        commit('setPackagesLoading', false)
        return response.package
      } else {
        const errorMsg = `Failed to install package ${name}`
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return null
      }
    },

    async uninstallPackage({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, name: string) {
      commit('setPackagesLoading', true)
      commit('setPackagesError', null)

      const response = await uninstallPackage(name)

      if (isApiError(response)) {
        const errorMsg = `Failed to uninstall package ${name}: ${response.message}`
        console.error(errorMsg)
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return false
      }

      if (response.success) {
        // Refresh packages list
        const packagesResponse = await getPackages()

        if (isApiError(packagesResponse)) {
          console.error('Error refreshing packages list:', packagesResponse.message)
        } else if (packagesResponse.success) {
          commit('setPackages', packagesResponse.packages)
        }

        dispatch('updateSnackbar', { text: `Package ${name} uninstalled successfully` }, { root: true })
        dispatch('addLogEvent', { title: 'Package Uninstalled', content: { name: name } }, { root: true })
        commit('setPackagesLoading', false)
        return true
      } else {
        const errorMsg = `Failed to uninstall package ${name}`
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return false
      }
    },

    async checkForUpdates({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, name?: string) {
      commit('setPackagesLoading', true)
      commit('setPackagesError', null)

      const response = await checkForUpdates(name)

      if (isApiError(response)) {
        const errorMsg = 'Failed to check for updates: ' + response.message
        console.error(errorMsg)
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg }, { root: true })
        commit('setPackagesLoading', false)
        return []
      }

      if (response.success) {
        commit('setPackagesLoading', false)
        return response.updates
      } else {
        const msg = 'Failed to check for updates'
        commit('setPackagesError', msg)
        dispatch('updateSnackbar', { text: msg }, { root: true })
        commit('setPackagesLoading', false)
        return []
      }
    },

    async upgradePackage(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { name, version }: { name: string; version?: string }
    ) {
      commit('setPackagesLoading', true)
      commit('setPackagesError', null)

      const response = await upgradePackage(name, version)

      if (isApiError(response)) {
        const errorMsg = `Failed to upgrade package ${name}: ${response.message}`
        console.error(errorMsg)
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return null
      }

      if (response.success) {
        // Refresh packages list
        const packagesResponse = await getPackages()

        if (isApiError(packagesResponse)) {
          console.error('Error refreshing packages list:', packagesResponse.message)
        } else if (packagesResponse.success) {
          commit('setPackages', packagesResponse.packages)
        }

        dispatch('updateSnackbar', { text: `Package ${name} upgraded successfully` }, { root: true })
        dispatch(
          'addLogEvent',
          {
            title: 'Package Upgraded',
            content: {
              name: name,
              version: version
            }
          },
          { root: true }
        )
        commit('setPackagesLoading', false)
        return response.package
      } else {
        const errorMsg = `Failed to upgrade package ${name}`
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return null
      }
    },

    async upgradeAllPackages({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setPackagesLoading', true)
      commit('setPackagesError', null)

      const response = await upgradeAllPackages()

      if (isApiError(response)) {
        const errorMsg = 'Failed to upgrade all packages: ' + response.message
        console.error(errorMsg)
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return []
      }

      if (response.success) {
        // Refresh packages list
        const packagesResponse = await getPackages()

        if (isApiError(packagesResponse)) {
          console.error('Error refreshing packages list:', packagesResponse.message)
        } else if (packagesResponse.success) {
          commit('setPackages', packagesResponse.packages)
        }

        dispatch('updateSnackbar', { text: 'All packages upgraded successfully' }, { root: true })
        dispatch(
          'addLogEvent',
          {
            title: 'All Packages Upgraded',
            content: {
              count: response.results.length
            }
          },
          { root: true }
        )
        commit('setPackagesLoading', false)
        return response.results
      } else {
        const errorMsg = 'Failed to upgrade all packages'
        commit('setPackagesError', errorMsg)
        dispatch('updateSnackbar', { text: errorMsg, color: 'error' }, { root: true })
        commit('setPackagesLoading', false)
        return []
      }
    },

    async callMcpTool(
      { commit, state, dispatch }: { commit: Commit; state: McpState; dispatch: Dispatch },
      payload: { serverId: string; toolId: string; args: Record<string, unknown> }
    ) {
      const { serverId, toolId, args } = payload
      const server = state.servers[serverId]
      const tool = state.tools[serverId]?.[toolId]

      if (!server || !tool) {
        const msg = 'Unable to run tool: server or tool not found'
        dispatch('updateSnackbar', { text: msg, color: 'error' }, { root: true })
        return { success: false as const, error: msg }
      }

      const serverName = server.name.trim()
      const methodName = tool.name.trim()
      const callKey = buildToolCallKey(serverName, methodName)

      if (server.enabled !== true) {
        const msg = `Server ${serverName} is disabled`
        commit('setToolCallError', { callKey, error: { success: false, error: msg } })
        dispatch('updateSnackbar', { text: msg, color: 'error' }, { root: true })
        return { success: false as const, error: msg }
      }

      const request: McpToolCallRequest = {
        serverName,
        methodName,
        args: args ?? {}
      }

      commit('setToolCallResult', { callKey, result: null })
      commit('setToolCallLoading', { callKey, loading: true })
      commit('setToolCallError', { callKey, error: null })
      commit('setToolCallArgs', { callKey, args: request.args })

      const response = await apiCallMcpTool(request)

      commit('setToolCallLoading', { callKey, loading: false })

      if (isApiError(response)) {
        commit('setToolCallError', { callKey, error: { success: false, error: response.message } })
        dispatch('updateSnackbar', { text: `MCP Tool Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'MCP Tool Error', content: response }, { root: true })
        return { success: false as const, error: response.message }
      }

      if (!response.success) {
        commit('setToolCallError', { callKey, error: response })
        dispatch('updateSnackbar', { text: `MCP Tool Error: ${response.error}`, color: 'error' }, { root: true })
        return response
      }

      commit('setToolCallResult', { callKey, result: response.data })
      dispatch(
        'addLogEvent',
        {
          title: 'MCP Tool Called',
          content: { serverName, methodName }
        },
        { root: true }
      )

      return response
    },
    clearToolCallState({ commit }: { commit: Commit }, callKey: string) {
      commit('clearToolCallState', callKey)
    },
    clearAllToolCallState({ commit }: { commit: Commit }) {
      commit('clearAllToolCallState')
    },
    async requestMcpServerInstall(
      { dispatch }: { dispatch: Dispatch },
      payload: RequestMcpServerInstallPayload
    ) {
      const response = await apiRequestMcpServerInstall(payload)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: 'Failed to submit MCP server request', color: 'error' })
        dispatch('addLogEvent', { title: 'MCP Server Request Error', content: response })
        return response
      }

      if (response.success) {
        dispatch('updateSnackbar', { text: 'MCP server request submitted' })
      } else {
        dispatch('updateSnackbar', { text: response.error || 'Failed to submit MCP server request', color: 'error' })
      }

      dispatch('addLogEvent', { title: 'MCP Server Request', content: { serverName: payload.serverName, requestId: response.requestId } })
      return response
    },

    /**
     * Save multiple secrets for an MCP server
     * @param context Vuex context
     * @param payload Object containing serverName and secrets array
     */
    async saveServerSecrets(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { serverName, secrets }: { serverName: string; secrets: Array<{name: string, value: string}> }
    ) {
      if (!serverName || !secrets || !Array.isArray(secrets)) {
        dispatch('updateSnackbar', { text: 'Invalid request: serverName and secrets are required' }, { root: true })
        return false
      }

      if (secrets.length > 5) {
        dispatch('updateSnackbar', { text: 'Maximum 5 secrets allowed per server' }, { root: true })
        return false
      }

      try {
        const response = await fetch(`/v1/mcp/servers/${encodeURIComponent(serverName)}/secrets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ secrets })
        })

        const data = await response.json()

        if (data.success) {
          dispatch('updateSnackbar', { text: `Successfully saved ${secrets.length} secret(s)` }, { root: true })
          dispatch(
            'addLogEvent',
            {
              title: 'MCP Secrets Saved',
              content: { serverName, secretCount: secrets.length }
            },
            { root: true }
          )
          return true
        } else {
          dispatch('updateSnackbar', { text: `Failed to save secrets: ${data.error}` }, { root: true })
          return false
        }
      } catch (error) {
        console.error('Error saving server secrets:', error)
        dispatch('updateSnackbar', { text: `Error saving secrets: ${error}` }, { root: true })
        return false
      }
    }
  },
  getters: {
    mcpServers: (state: McpState) => state.servers,
    mcpTools: (state: McpState) => state.tools,
    mcpServerById: (state: McpState) => (id: string) => state.servers[id],
    mcpToolsForServer: (state: McpState) => (serverId: string) => state.tools[serverId] || {},
    mcpError: (state: McpState) => state.error,
    mcpLoading: (state: McpState) => state.loading,

    // Available MCP servers getters
    availableServers: (state: McpState) => state.availableServers,
    availableServersError: (state: McpState) => state.availableServersError,
    availableServersLoading: (state: McpState) => state.availableServersLoading,
    availableServersSearchQuery: (state: McpState) => state.availableServersSearchQuery,

    // MCP Packages getters
    mcpPackages: (state: McpState) => state.packages,
    mcpPackageByName: (state: McpState) => (name: string) => state.packages.find((pkg) => pkg.name === name),
    mcpPackagesError: (state: McpState) => state.packagesError,
    mcpPackagesLoading: (state: McpState) => state.packagesLoading,

    mcpToolCallLoadingByKey: (state: McpState) => state.toolCallLoadingByKey,
    mcpToolCallErrorByKey: (state: McpState) => state.toolCallErrorByKey,
    mcpToolCallResultByKey: (state: McpState) => state.toolCallResultByKey,
    mcpToolCallArgsByKey: (state: McpState) => state.toolCallArgsByKey,
    mcpToolCallStateByKey:
      (state: McpState) =>
      (callKey: string) => ({
        loading: state.toolCallLoadingByKey[callKey] === true,
        error: state.toolCallErrorByKey[callKey] || null,
        result: state.toolCallResultByKey[callKey],
        args: state.toolCallArgsByKey[callKey] || {}
      }),

    // Agent tools getter - provides tools from all enabled MCP servers in the format expected by AgentCard
    enabledServers: (state: McpState) => {
      return Object.values(state.servers).filter((server) => {
        if (server.enabled !== true) {
          return false
        }

        if (server.source !== 'external') {
          return true
        }

        if (server.installed !== true) {
          return false
        }

        if (server.authMode === 'oauth2') {
          return server.authState === 'connected'
        }

        return true
      })
    }
  }
})

/**
 * Transform the tools list from the API response to the format expected by the components
 */
function transformToolsToMcpTools(toolsList: ToolMethod[]): Record<string, McpTool> {
  const result: Record<string, McpTool> = {}

  toolsList.forEach((tool, index) => {
    const toolId = `tool-${index}-${Date.now()}`
    const parameters: McpToolParameter[] = []

    // Extract parameters from inputSchema
    if (tool.inputSchema && tool.inputSchema.properties) {
      Object.entries(tool.inputSchema.properties).forEach(([paramName, paramSchema]) => {
        const schemaEntry = paramSchema as Record<string, unknown>
        const required = tool.inputSchema.required?.includes(paramName) || false
        const normalizedType = normalizeParameterType(schemaEntry.type)
        const defaultValue =
          Object.prototype.hasOwnProperty.call(schemaEntry, 'default') && schemaEntry.default !== null
            ? schemaEntry.default
            : undefined
        const exampleValue = inferParameterExampleValue(paramName, normalizedType, schemaEntry)
        const enumValues = Array.isArray(schemaEntry.enum) ? schemaEntry.enum : undefined
        parameters.push({
          name: paramName,
          type: normalizedType,
          description: (typeof schemaEntry.description === 'string' ? schemaEntry.description : ''),
          required,
          defaultValue,
          exampleValue,
          enumValues
        })
      })
    }

    result[toolId] = {
      id: toolId,
      name: tool.name,
      description: tool.description || '',
      parameters,
      example: JSON.stringify({ [tool.name]: { ...generateExampleParams(parameters) } }, null, 2)
    }
  })

  return result
}

/**
 * Detect npm package from command arguments
 */
function detectNpmPackage(args: string[]): { name: string; url: string } | null {
  if (!args || args.length === 0) return null

  const firstArg = args[0]
  if (!firstArg || !firstArg.includes('node_modules')) return null

  const parts = firstArg.split('/')
  const nodeModulesIndex = parts.findIndex((part) => part === 'node_modules')

  if (nodeModulesIndex === -1 || nodeModulesIndex >= parts.length - 1) return null

  // Check if next part starts with @, indicating an org package
  const nextPart = parts[nodeModulesIndex + 1]
  if (nextPart.startsWith('@')) {
    // Need two parts for org packages: @org/package
    if (nodeModulesIndex + 2 >= parts.length) return null

    const orgName = nextPart
    const packageName = parts[nodeModulesIndex + 2]
    const fullName = `${orgName}/${packageName}`
    return {
      name: fullName,
      url: `https://www.npmjs.com/package/${fullName}`
    }
  } else {
    // Regular package
    return {
      name: nextPart,
      url: `https://www.npmjs.com/package/${nextPart}`
    }
  }
}

/**
 * Generate example parameter values based on parameter types
 */
function generateExampleParams(parameters: McpToolParameter[]): Record<string, any> {
  const result: Record<string, any> = {}

  parameters.forEach((param) => {
    if (param.defaultValue !== undefined) {
      result[param.name] = param.defaultValue
      return
    }
    if (param.exampleValue !== undefined) {
      result[param.name] = param.exampleValue
      return
    }
    if (!param.required) {
      return
    }
    if (param.type === 'number') {
      result[param.name] = 42
    } else if (param.type === 'boolean') {
      result[param.name] = true
    } else if (param.type === 'object') {
      result[param.name] = { key: 'value' }
    } else if (param.type === 'array') {
      result[param.name] = ['example']
    } else if (param.type === 'string') {
      result[param.name] = 'example'
    }
  })

  return result
}

function inferParameterExampleValue(
  paramName: string,
  normalizedType: string,
  schemaEntry: Record<string, unknown>
): unknown {
  if (schemaEntry.example !== undefined && schemaEntry.example !== null) {
    return schemaEntry.example
  }

  if (Array.isArray(schemaEntry.examples)) {
    const firstNonNullExample = schemaEntry.examples.find((value) => value !== null && value !== undefined)
    if (firstNonNullExample !== undefined) {
      return firstNonNullExample
    }
  }

  if (Array.isArray(schemaEntry.enum) && schemaEntry.enum.length > 0) {
    const firstEnumValue = schemaEntry.enum.find((value) => value !== null && value !== undefined)
    if (firstEnumValue !== undefined) {
      return firstEnumValue
    }
  }

  if (normalizedType === 'array') {
    const items = typeof schemaEntry.items === 'object' && schemaEntry.items !== null
      ? (schemaEntry.items as Record<string, unknown>)
      : undefined
    if (items && Array.isArray(items.enum) && items.enum.length > 0) {
      const firstItemEnumValue = items.enum.find((value) => value !== null && value !== undefined)
      if (firstItemEnumValue !== undefined) {
        return [firstItemEnumValue]
      }
    }
  }

  const loweredName = paramName.toLowerCase()
  if (loweredName === 'model') return 'claude'
  if (loweredName === 'name') return 'Header Promo'
  if (loweredName === 'block_type' || loweredName === 'type') return 'html'
  if (loweredName === 'content_type') return 'block'
  if (loweredName.endsWith('_id') || loweredName === 'id') return 'uc_123'
  if (loweredName === 'fields') return ['name', 'definition.data.content']
  if (loweredName === 'sort') return 'name'
  if (loweredName === 'page_size') return 20
  if (loweredName === 'page_cursor') return 'next-page-cursor'

  return undefined
}

function normalizeParameterType(rawType: unknown): string {
  if (typeof rawType === 'string') {
    return rawType === 'integer' ? 'number' : rawType
  }

  if (Array.isArray(rawType)) {
    const firstNonNull = rawType.find((candidate) => typeof candidate === 'string' && candidate !== 'null')
    if (typeof firstNonNull === 'string') {
      return firstNonNull === 'integer' ? 'number' : firstNonNull
    }
  }

  return 'string'
}

/**
 * Transform MCP tools to the format expected by AgentCard
 */
function transformMcpToolsToAgentTools(mcpTools: Record<string, McpTool>): Tool {
  const result: Tool = {}

  Object.values(mcpTools).forEach((tool) => {
    // Create a ToolMethod for each McpTool
    const toolMethod: ToolMethod = {
      name: tool.name,
      description: tool.description,
      inputSchema: {
        type: 'object',
        properties: tool.parameters.reduce((acc, param) => {
          acc[param.name] = {
            type: param.type,
            description: param.description
          }
          return acc
        }, {} as Record<string, any>),
        required: tool.parameters.filter((p) => p.required).map((p) => p.name),
        additionalProperties: false
      }
    }

    // Add the ToolMethod to the Tool
    if (!result[tool.name]) {
      result[tool.name] = []
    }
    result[tool.name].push(toolMethod)
  })

  return result
}
